import * as mediasoupClient from "mediasoup-client";
import protooClient from "protoo-client";
import { debug as newDebug } from "debug";
import EventEmitter from "eventemitter3";
import { MediaDevices, MediaDevicesEvents } from "./utils/media-devices-utils";
import { MIC_AUDIO_PROCESSING_LABELS } from "./utils/mic-audio-processing";
import { recoverRtcSfuConnection, RTC_SFU_RECONNECTION_STATE } from "./utils/rtc-sfu-reconnection-utils";
import { resolveDialogIceServers } from "./utils/dialog-ice-servers";
import {
  DIALOG_MEDIA_QUALITY_CHANGED,
  cameraProducerOptionsForMediaQuality,
  normalizeDialogMediaQualityProfile,
  normalizeDialogMediaQualityState,
  rtpEncodingParametersForMediaQuality,
  screenProducerOptionsForMediaQuality
} from "./utils/dialog-media-quality";
import {
  getRtcSfuProtooRequestTimeoutMs,
  logRtcSfuRecovery,
  summarizeRtcSfuEndpoint,
  summarizeRtcSfuError,
  summarizeRtcSfuRoomSession,
  summarizeRtcSfuTrack
} from "./utils/rtc-sfu-recovery-debug";
import {
  ROOM_RESOURCE_STATE_CHANGED,
  RoomResourceIds,
  applyRoomResourceLimits,
  getRoomResource,
  isRoomResourceAvailable
} from "./utils/room-resource-state-utils";
import { RoomResourceLimitKeys } from "./utils/room-resource-limits-utils";
import { resolveRoomUserId } from "./utils/room-persistence-utils";

// Used for VP9 webcam video.
//const VIDEO_KSVC_ENCODINGS = [{ scalabilityMode: "S3T3_KEY" }];

// Used for VP9 desktop sharing.
//const VIDEO_SVC_ENCODINGS = [{ scalabilityMode: "S3T3", dtx: true }];

// TODO
// - look into requestConsumerKeyframe
// - look into applyNetworkThrottle
// SFU todo
// - remove active speaker stuff
// - remove score stuff

// Based upon mediasoup-demo RoomClient

const debug = newDebug("naf-dialog-adapter:debug");
//const warn = newDebug("naf-dialog-adapter:warn");
const error = newDebug("naf-dialog-adapter:error");
const info = newDebug("naf-dialog-adapter:info");

const PC_PROPRIETARY_CONSTRAINTS = {
  optional: [{ googDscp: true }]
};

function getMediaRequestKey(kind, source) {
  return source ? `${kind}:${source}` : kind;
}

function getVideoSourceForTrack(track, appData = {}) {
  if (appData?.share === true || track?._hubs_contentHint === MediaDevices.SCREEN) {
    return MediaDevices.SCREEN;
  }

  if (track?.kind === "video") {
    return MediaDevices.CAMERA;
  }

  return null;
}

function applyScreenShareContentHint(track) {
  if (!track) return;

  try {
    track.contentHint = "detail";
  } catch (e) {
    // Some browser/track implementations expose contentHint as read-only.
  }
}

function matchesRequestedMediaSource(track, appData, source) {
  if (!source || track?.kind !== "video") {
    return true;
  }

  return getVideoSourceForTrack(track, appData) === source;
}

function summarizeDialogRequestPayload(payload = {}) {
  if (!payload) {
    return null;
  }

  return {
    clientMessageId: payload.clientMessageId,
    language: payload.language,
    hasText: typeof payload.text === "string",
    textLength: typeof payload.text === "string" ? payload.text.length : undefined,
    hasMetadata: Boolean(payload.metadata),
    recipientSessionId: payload.recipientSessionId,
    agentUuid: payload.agentUuid,
    senderType: payload.senderType,
    turnId: payload.turnId,
    source: payload.source,
    profile: payload.profile
  };
}

function summarizeDialogResponse(response = {}) {
  if (!response || typeof response !== "object") {
    return { hasResponse: response !== undefined };
  }

  return {
    hasResponse: true,
    conversationType: response.conversationType,
    senderSessionId: response.senderSessionId,
    recipientSessionId: response.recipientSessionId,
    clientMessageId: response.clientMessageId,
    idempotencyKey: response.idempotencyKey,
    createdAt: response.createdAt
  };
}

function summarizeLocalMediaTracks(tracks = []) {
  return {
    trackCount: tracks.length,
    audioTrackCount: tracks.filter(track => track.kind === "audio").length,
    videoTrackCount: tracks.filter(track => track.kind === "video").length,
    liveTrackCount: tracks.filter(track => track.readyState === "live").length,
    enabledTrackCount: tracks.filter(track => track.enabled).length
  };
}

function createProtooRequestTimeoutError(type, timeoutMs) {
  const error = new Error(`RTC SFU protoo request timed out after ${timeoutMs}ms.`);
  error.code = "RTC_SFU_PROTOO_REQUEST_TIMEOUT";
  error.type = type;
  error.timeoutMs = timeoutMs;

  return error;
}

function createProtooDisconnectedError(type, reason) {
  const error = new Error(`RTC SFU protoo request interrupted by signaling ${reason}.`);
  error.code = "RTC_SFU_PROTOO_DISCONNECTED";
  error.type = type;
  error.reason = reason;

  return error;
}

export const DIALOG_CONNECTION_CONNECTED = "dialog-connection-connected";
export const DIALOG_CONNECTION_ERROR_FATAL = "dialog-connection-error-fatal";
export const DIALOG_CONNECTION_STATE_CHANGED = "dialog-connection-state-changed";
export const DIALOG_CONNECTION_STATE = RTC_SFU_RECONNECTION_STATE;
export const DIALOG_CHAT_MESSAGE_CREATED = "dialog-chat-message-created";

const PRIVATE_CHAT_NOTIFICATION_EVENTS = {
  privateChatMessageStarted: "private-chat-message-started",
  privateChatMessageDelta: "private-chat-message-delta",
  privateChatMessageCompleted: "private-chat-message-completed",
  privateChatMessageError: "private-chat-message-error"
};

export class DialogAdapter extends EventEmitter {
  constructor() {
    super();

    this._micShouldBeEnabled = false;
    this._micProducer = null;
    this._cameraProducer = null;
    this._shareProducer = null;
    this._localMediaStream = null;
    this._consumers = new Map();
    this._pendingMediaRequests = new Map();
    this._blockedClients = new Map();
    this._forceTcp = false;
    this._forceTurn = false;
    this._iceTransportPolicy = null;
    this.scene = null;
    this._serverParams = {};
    this._consumerStats = {};
    this._dialogEndpoint = null;
    this._resolveDialogEndpoint = null;
    this._resolveRoomSession = null;
    this._recovering = false;
    this._disconnecting = false;
    this._recoveryOptions = {};
    this._allocationGrant = null;
    this._peerMode = "active";
    this._roomSessionId = null;
    this._reconnectToken = null;
    this._roomResourceState = null;
    this._roomResourceLimits = null;
    this._roomMediaQuality = normalizeDialogMediaQualityState(null);
    this._authRecoveryPromise = null;
    this._dialogAuthToken = null;
    this._dialogJoined = false;
    this._pendingProtooRequests = new Set();
    this._signalingReadyPromise = null;
    this._resolveSignalingReady = null;
  }

  get consumerStats() {
    return this._consumerStats;
  }

  get downlinkBwe() {
    return this._downlinkBwe;
  }

  handlePrivateChatNotification(notification) {
    const eventName = PRIVATE_CHAT_NOTIFICATION_EVENTS[notification.method];

    if (!eventName) {
      return false;
    }

    this.emit(eventName, notification.data);
    return true;
  }

  get roomResourceState() {
    return this._effectiveRoomResourceState();
  }

  get hasRoomResourceState() {
    return Boolean(this._roomResourceState);
  }

  hasRoomResourceLimit(resourceId) {
    const limitKey = RoomResourceLimitKeys[resourceId];

    return limitKey
      ? this._roomResourceLimits?.[limitKey] !== undefined && this._roomResourceLimits?.[limitKey] !== null
      : false;
  }

  getRoomResource(resourceId) {
    return getRoomResource(this.roomResourceState, resourceId);
  }

  canAcquireRoomResource(resourceId) {
    return isRoomResourceAvailable(this.roomResourceState, resourceId);
  }

  get roomMediaQuality() {
    return this._roomMediaQuality;
  }

  _getDisplayName() {
    return APP.store?.state?.profile?.displayName || this._clientId;
  }

  getIceServers(host, port, turn, iceServers) {
    return resolveDialogIceServers({
      host,
      turn,
      iceServers,
      forceTcp: this._forceTcp
    });
  }

  /**
   * Gets transport/consumer/producer stats on the server side.
   */
  async getServerStats() {
    if (!this._protoo.connected) {
      // Signaling channel not connected, no reason to get remote RTC stats.
      return;
    }

    const result = {};
    try {
      if (!this._sendTransport?._closed) {
        const sendTransport = (result[this._sendTransport.id] = {});
        sendTransport.name = "Send";
        sendTransport.stats = await this._protoo.request("getTransportStats", {
          transportId: this._sendTransport.id
        });
        result[this._sendTransport.id]["producers"] = {};
        for (const producer of this._sendTransport._producers) {
          const id = producer[0];
          result[this._sendTransport.id]["producers"][id] = await this._protoo.request("getProducerStats", {
            producerId: id
          });
        }
      }
      if (!this._recvTransport?._closed) {
        const recvTransport = (result[this._recvTransport.id] = {});
        recvTransport.name = "Receive";
        recvTransport.stats = await this._protoo.request("getTransportStats", {
          transportId: this._recvTransport.id
        });
        result[this._recvTransport.id]["consumers"] = {};
        for (const consumer of this._recvTransport._consumers) {
          const id = consumer[0];
          result[this._recvTransport.id]["consumers"][id] = await this._protoo.request("getConsumerStats", {
            consumerId: id
          });
        }
      }
      return result;
    } catch (e) {
      this.emitRTCEvent("error", "Adapter", () => `Error getting the server status: ${e}`);
      return { error: `Error getting the server status: ${e}` };
    }
  }

  async iceRestart(transport) {
    // Force an ICE restart to gather new candidates and trigger a reconnection
    logRtcSfuRecovery("ice-restart:start", {
      transportId: transport?.id,
      transportRole: transport?.id === this._sendTransport?.id ? "send" : "receive",
      protooConnected: Boolean(this._protoo?.connected)
    });
    this.emitRTCEvent(
      "log",
      "RTC",
      () => `Restarting ${transport.id === this._sendTransport.id ? "send" : "receive"} transport ICE`
    );
    const iceParameters = await this._requestProtoo("restartIce", { transportId: transport.id });
    await transport.restartIce({ iceParameters });
    logRtcSfuRecovery("ice-restart:done", {
      transportId: transport?.id,
      transportRole: transport?.id === this._sendTransport?.id ? "send" : "receive"
    });
  }

  async recreateSendTransport(iceServers) {
    this.emitRTCEvent("log", "RTC", () => `Recreating send transport ICE`);
    await this.closeSendTransport();
    await this.createSendTransport(iceServers);
    if (this._localMediaStream) {
      await this.setLocalMediaStream(this._localMediaStream);
    }
  }

  /**
   * Restart ICE in the underlying send peerconnection.
   */
  async restartSendICE() {
    // Do not restart ICE if Signaling is disconnected.
    if (!this._protoo || !this._protoo.connected) {
      logRtcSfuRecovery("ice-restart:send-skipped", {
        reason: "signaling-disconnected",
        hasProtoo: Boolean(this._protoo),
        protooConnected: Boolean(this._protoo?.connected)
      });
      return;
    }

    try {
      if (!this._sendTransport?._closed) {
        await this.iceRestart(this._sendTransport);
      } else {
        // If the transport is closed but the signaling is connected, we try to recreate
        const { host, port, turn, iceServers, ice_servers } = this._serverParams;
        const resolvedIceServers = this.getIceServers(host, port, turn, iceServers || ice_servers);
        await this.recreateSendTransport(resolvedIceServers);
      }
    } catch (err) {
      logRtcSfuRecovery("ice-restart:send-error", { error: summarizeRtcSfuError(err) });
      this.emitRTCEvent("error", "RTC", () => `Send transport [recreate] failed: ${err}`);
    }
  }

  /**
   * Checks the Send Transport ICE status and restarts it in case is in failed or disconnected state.
   * This is called by the Send Transport "connectionstatechange" event listener.
   * @param {boolean} connectionState The transport connnection state (ICE connection state)
   */
  checkSendIceStatus(connectionState) {
    // If the ICE connection is broken or interrupted, force an ICE restart.
    if (connectionState === "failed" || connectionState === "disconnected") {
      this.restartSendICE();
    }
  }

  async recreateRecvTransport(iceServers) {
    this.emitRTCEvent("log", "RTC", () => `Recreating receive transport ICE`);
    await this.closeRecvTransport();
    await this.createRecvTransport(iceServers);
    await this._protoo.request("refreshConsumers");
  }

  /**
   * Restart ICE in the underlying receive peerconnection.
   * @param {boolean} force Forces the execution of the reconnect.
   */
  async restartRecvICE() {
    if (!this._protoo || !this._protoo.connected) {
      logRtcSfuRecovery("ice-restart:recv-skipped", {
        reason: "signaling-disconnected",
        hasProtoo: Boolean(this._protoo),
        protooConnected: Boolean(this._protoo?.connected)
      });
      return;
    }

    try {
      if (!this._recvTransport?._closed) {
        await this.iceRestart(this._recvTransport);
      } else {
        // If the transport is closed but the signaling is connected, we try to recreate
        const { host, port, turn, iceServers, ice_servers } = this._serverParams;
        const resolvedIceServers = this.getIceServers(host, port, turn, iceServers || ice_servers);
        await this.recreateRecvTransport(resolvedIceServers);
      }
    } catch (err) {
      logRtcSfuRecovery("ice-restart:recv-error", { error: summarizeRtcSfuError(err) });
      this.emitRTCEvent("error", "RTC", () => `Receive transport [recreate] failed: ${err}`);
    }
  }

  /**
   * Checks the ReeceiveReeceive Transport ICE status and restarts it in case is in failed or disconnected state.
   * This is called by the Reeceive Transport "connectionstatechange" event listener.
   * @param {boolean} connectionState The transport connection state (ICE connection state)
   */
  checkRecvIceStatus(connectionState) {
    // If the ICE connection is broken or interrupted, force an ICE restart.
    if (connectionState === "failed" || connectionState === "disconnected") {
      this.restartRecvICE();
    }
  }

  async connect({
    serverUrl,
    roomId,
    serverParams,
    scene,
    clientId,
    forceTcp,
    forceTurn,
    iceTransportPolicy,
    dialogEndpoint,
    resolveDialogEndpoint,
    recoveryOptions,
    suppressRecovery = false,
    allocationGrant,
    peerMode,
    roomSessionId,
    reconnectToken,
    roomResourceLimits,
    roomMediaQuality,
    resolveRoomSession
  }) {
    if (this._roomId && this._roomId !== roomId) {
      this._roomResourceState = null;
      this._roomResourceLimits = null;
      this._roomMediaQuality = normalizeDialogMediaQualityState(null);
      this._dialogAuthToken = null;
    }

    this._disconnecting = false;
    this._serverUrl = serverUrl;
    this._roomId = roomId;
    this._serverParams = serverParams;
    this._clientId = clientId;
    this.scene = scene;
    this._forceTcp = forceTcp;
    this._forceTurn = forceTurn;
    this._iceTransportPolicy = iceTransportPolicy;
    this._dialogEndpoint = dialogEndpoint || {
      host: serverParams.host,
      port: serverParams.port,
      turn: serverParams.turn,
      serverUrl,
      allocation: serverParams.allocation,
      usingRtcSfuOrchestrator: Boolean(serverParams.allocation)
    };

    if (resolveDialogEndpoint) {
      this._resolveDialogEndpoint = resolveDialogEndpoint;
    }
    if (resolveRoomSession) {
      this._resolveRoomSession = resolveRoomSession;
    }
    if (recoveryOptions) {
      this._recoveryOptions = recoveryOptions;
    }
    this._allocationGrant = allocationGrant || null;
    this._peerMode = peerMode || "active";
    this._roomSessionId = roomSessionId || null;
    this._reconnectToken = reconnectToken || null;
    this._roomResourceLimits = roomResourceLimits || null;
    this._roomMediaQuality = normalizeDialogMediaQualityState(roomMediaQuality, this._roomMediaQuality);
    this._dialogJoined = false;
    this._markSignalingUnavailable("connecting");

    logRtcSfuRecovery("dialog-connect:start", {
      roomId: this._roomId,
      clientId: this._clientId,
      endpoint: summarizeRtcSfuEndpoint(this._dialogEndpoint),
      suppressRecovery,
      hasAllocationGrant: Boolean(this._allocationGrant),
      peerMode: this._peerMode,
      roomSessionId: this._roomSessionId,
      hasReconnectToken: Boolean(this._reconnectToken),
      hasResolveDialogEndpoint: Boolean(this._resolveDialogEndpoint),
      hasResolveRoomSession: Boolean(this._resolveRoomSession),
      forceTcp: Boolean(this._forceTcp),
      forceTurn: Boolean(this._forceTurn),
      iceTransportPolicy: this._iceTransportPolicy
    });

    const urlWithParams = new URL(this._serverUrl);
    urlWithParams.searchParams.append("roomId", this._roomId);
    urlWithParams.searchParams.append("peerId", this._clientId);

    // TODO: Establishing connection could take a very long time.
    //       Inform the user if we are stuck here.
    const protooTransport = new protooClient.WebSocketTransport(urlWithParams.toString(), {
      retry: { retries: 2 }
    });
    this._protoo = new protooClient.Peer(protooTransport);

    this._protoo.on("disconnected", () => {
      this._dialogJoined = false;
      this._markSignalingUnavailable("disconnected");
      logRtcSfuRecovery("protoo:disconnected", {
        roomId: this._roomId,
        endpoint: summarizeRtcSfuEndpoint(this._dialogEndpoint),
        sendTransportState: this._sendTransport?.connectionState,
        recvTransportState: this._recvTransport?.connectionState
      });
      this.emitRTCEvent("info", "Signaling", () => `Disconnected`);
      this.cleanUpLocalState();
    });

    this._protoo.on("failed", attempt => {
      logRtcSfuRecovery("protoo:failed", {
        roomId: this._roomId,
        attempt,
        endpoint: summarizeRtcSfuEndpoint(this._dialogEndpoint)
      });
      this.emitRTCEvent("error", "Signaling", () => `Failed: ${attempt}, retrying...`);
    });

    // eslint-disable-next-line no-unused-vars
    this._protoo.on("request", async (request, accept, reject) => {
      this.emitRTCEvent("info", "Signaling", () => `Request [${request.method}]: ${request.data?.id}`);
      debug('proto "request" event [method:%s, data:%o]', request.method, request.data?.id);

      switch (request.method) {
        case "newConsumer": {
          const { peerId, producerId, id, kind, rtpParameters, /*type, */ appData /*, producerPaused */ } =
            request.data;

          try {
            const consumer = await this._recvTransport.consume({
              id,
              producerId,
              kind,
              rtpParameters,
              appData: { ...appData, peerId, producerId } // Trick.
            });

            // Store in the map.
            this._consumers.set(consumer.id, consumer);

            consumer.on("transportclose", () => {
              this.emitRTCEvent("error", "RTC", () => `Consumer transport closed`);
              this.removeConsumer(consumer.id);
            });

            if (kind === "video") {
              const { spatialLayers, temporalLayers } = mediasoupClient.parseScalabilityMode(
                consumer.rtpParameters.encodings[0].scalabilityMode
              );

              this._consumerStats[consumer.id] = this._consumerStats[consumer.id] || {};
              this._consumerStats[consumer.id]["spatialLayers"] = spatialLayers;
              this._consumerStats[consumer.id]["temporalLayers"] = temporalLayers;
            }

            // We are ready. Answer the protoo request so the server will
            // resume this Consumer (which was paused for now if video).
            accept();

            this.resolvePendingMediaRequestForTrack(peerId, consumer.track, consumer.appData);

            // Notify of an stream update event
            this.emit("stream_updated", peerId, kind, getVideoSourceForTrack(consumer.track, consumer.appData));
          } catch (err) {
            this.emitRTCEvent("error", "Adapter", () => `Error: ${err}`);
            error('"newConsumer" request failed:%o', err);

            throw err;
          }

          break;
        }
      }
    });

    this._protoo.on("notification", notification => {
      debug('proto "notification" event [method:%s, data:%o]', notification.method, notification.data);

      if (this.handlePrivateChatNotification(notification)) {
        return;
      }

      switch (notification.method) {
        case "newPeer": {
          break;
        }

        case "peerClosed": {
          const { peerId } = notification.data;
          this.closePeer(peerId);

          break;
        }

        case "consumerClosed": {
          const { consumerId } = notification.data;
          const consumer = this._consumers.get(consumerId);

          if (!consumer) {
            info(`consumerClosed event received without related consumer: ${consumerId}`);
            break;
          }

          consumer.close();
          this.removeConsumer(consumer.id);

          break;
        }

        case "peerBlocked": {
          const { peerId } = notification.data;
          document.body.dispatchEvent(new CustomEvent("blocked", { detail: { clientId: peerId } }));

          break;
        }

        case "peerUnblocked": {
          const { peerId } = notification.data;
          document.body.dispatchEvent(new CustomEvent("unblocked", { detail: { clientId: peerId } }));

          break;
        }

        case "downlinkBwe": {
          this._downlinkBwe = notification.data;
          break;
        }

        case "consumerLayersChanged": {
          const { consumerId, spatialLayer, temporalLayer } = notification.data;

          const consumer = this._consumers.get(consumerId);

          if (!consumer) {
            info(`consumerLayersChanged event received without related consumer: ${consumerId}`);
            break;
          }

          this._consumerStats[consumerId] = this._consumerStats[consumerId] || {};
          this._consumerStats[consumerId]["spatialLayer"] = spatialLayer;
          this._consumerStats[consumerId]["temporalLayer"] = temporalLayer;

          // TODO: If spatialLayer/temporalLayer are null, that's probably because the current downlink
          // it's not enough forany spatial layer bitrate. In that case the server has paused the consumer.
          // At this point we it would be nice to give the user some visual cue that this stream is paused.
          // ie. A grey overlay with some icon or replacing the video stream por a generic person image.
          break;
        }

        case "consumerScore": {
          const { consumerId, score } = notification.data;

          const consumer = this._consumers.get(consumerId);

          if (!consumer) {
            info(`consumerScore event received without related consumer: ${consumerId}`);
            break;
          }

          this._consumerStats[consumerId] = this._consumerStats[consumerId] || {};
          this._consumerStats[consumerId]["score"] = score;
          break;
        }

        case "roomResourceStateChanged": {
          this._roomResourceState = notification.data || null;
          this.emit(ROOM_RESOURCE_STATE_CHANGED, this.roomResourceState);
          this.scene?.emit?.("room_resource_state_changed", this.roomResourceState);
          break;
        }

        case "roomMediaQualityChanged": {
          this.handleRoomMediaQualityChanged(notification.data);
          break;
        }

        case "peerMediaModerated": {
          this.handlePeerMediaModerated(notification.data);
          break;
        }

        case "chatMessageCreated": {
          this.emit(DIALOG_CHAT_MESSAGE_CREATED, notification.data);
          break;
        }
      }
    });

    return new Promise((resolve, reject) => {
      let opened = false;

      this._protoo.on("close", async () => {
        // We explicitly disconnect event handlers when closing the socket ourselves,
        // so if we get into here, we were not the ones closing the connection.
        this._dialogJoined = false;
        this._markSignalingUnavailable("closed");
        logRtcSfuRecovery("protoo:close", {
          roomId: this._roomId,
          opened,
          suppressRecovery,
          disconnecting: this._disconnecting,
          recovering: this._recovering,
          endpoint: summarizeRtcSfuEndpoint(this._dialogEndpoint)
        });
        this.emitRTCEvent("error", "Signaling", () => `Closed`);

        if (!opened) {
          const err = new Error("RTC SFU signaling closed before connection completed.");
          logRtcSfuRecovery("dialog-connect:closed-before-open", {
            roomId: this._roomId,
            suppressRecovery,
            error: summarizeRtcSfuError(err)
          });
          if (!suppressRecovery) {
            this.emit(DIALOG_CONNECTION_ERROR_FATAL, { error: err, duringRecovery: false });
          }
          this._markSignalingReady(false);
          reject(err);
          return;
        }

        if (suppressRecovery || this._disconnecting || this._recovering) {
          return;
        }

        try {
          await this._recoverFromUnexpectedClose();
        } catch (err) {
          logRtcSfuRecovery("recovery:unexpected-close-error", { error: summarizeRtcSfuError(err) });
          this.emit(DIALOG_CONNECTION_ERROR_FATAL, { error: err, duringRecovery: false });
        }
      });

      this._protoo.on("open", async () => {
        logRtcSfuRecovery("protoo:open", {
          roomId: this._roomId,
          endpoint: summarizeRtcSfuEndpoint(this._dialogEndpoint),
          suppressRecovery
        });
        this.emitRTCEvent("info", "Signaling", () => `Open`);
        opened = true;

        try {
          await this._joinRoom();
          this._dialogJoined = true;
          this._markSignalingReady();
          logRtcSfuRecovery("dialog-connect:joined", {
            roomId: this._roomId,
            endpoint: summarizeRtcSfuEndpoint(this._dialogEndpoint),
            hasSendTransport: Boolean(this._sendTransport),
            hasRecvTransport: Boolean(this._recvTransport)
          });
          resolve();
          this.emit(DIALOG_CONNECTION_CONNECTED);
        } catch (err) {
          this._dialogJoined = false;
          this._markSignalingReady(false);
          logRtcSfuRecovery("dialog-connect:join-error", {
            roomId: this._roomId,
            endpoint: summarizeRtcSfuEndpoint(this._dialogEndpoint),
            suppressRecovery,
            error: summarizeRtcSfuError(err)
          });
          this.emitRTCEvent("warn", "Adapter", () => `Error during connect: ${error}`);
          reject(err);
          if (!suppressRecovery) {
            this.emit(DIALOG_CONNECTION_ERROR_FATAL, { error: err, duringRecovery: false });
          }
        }
      });
    });
  }

  _isRtcSfuOrchestratorEndpoint() {
    return Boolean(this._dialogEndpoint?.usingRtcSfuOrchestrator || this._dialogEndpoint?.allocation);
  }

  _emitConnectionState(state) {
    logRtcSfuRecovery("recovery:state", { state, roomId: this._roomId });
    this.emit(DIALOG_CONNECTION_STATE_CHANGED, { state });
    this.scene?.emit("rtc_sfu_connection_state_changed", { state });
  }

  _effectiveRoomResourceState() {
    return applyRoomResourceLimits(this._roomResourceState, this._roomResourceLimits);
  }

  setRoomResourceLimits(roomResourceLimits) {
    this._roomResourceLimits = roomResourceLimits || null;

    if (this._roomResourceState) {
      this.emit(ROOM_RESOURCE_STATE_CHANGED, this.roomResourceState);
      this.scene?.emit?.("room_resource_state_changed", this.roomResourceState);
    }
  }

  handleRoomMediaQualityChanged(profile, { apply = true } = {}) {
    this._roomMediaQuality = normalizeDialogMediaQualityState(profile, this._roomMediaQuality);

    if (apply) {
      this.applyRoomMediaQualityToLocalProducers().catch(err => {
        this.emitRTCEvent("error", "Adapter", () => `Media quality update failed: ${err}`);
      });
    }

    this.emit(DIALOG_MEDIA_QUALITY_CHANGED, this._roomMediaQuality);
    this.scene?.emit?.("dialog_media_quality_changed", this._roomMediaQuality);

    return this._roomMediaQuality;
  }

  async setRoomMediaQuality(profile) {
    await this.refreshAuthToken({ fetchPermissions: true });

    const requestedProfile = normalizeDialogMediaQualityProfile(profile);
    const response = await this._requestWithAuthRecovery("setRoomMediaQuality", {
      profile: requestedProfile.profile
    });
    const appliedProfile = this.handleRoomMediaQualityChanged(response, { apply: false });

    await this.applyRoomMediaQualityToLocalProducers();

    return appliedProfile;
  }

  async applyRoomMediaQualityToLocalProducers() {
    await Promise.all([
      this._applyProducerMediaQuality(this._cameraProducer, MediaDevices.CAMERA),
      this._applyProducerMediaQuality(this._shareProducer, MediaDevices.SCREEN)
    ]);
  }

  _isScreenShareActive() {
    return Boolean(this._shareProducer && !this._shareProducer.closed);
  }

  async _applyProducerMediaQuality(producer, source) {
    if (!producer || producer.closed || typeof producer.setRtpEncodingParameters !== "function") {
      return;
    }

    if (source === MediaDevices.SCREEN) {
      applyScreenShareContentHint(producer.track);
    }

    await producer.setRtpEncodingParameters(
      rtpEncodingParametersForMediaQuality(this._roomMediaQuality, source, {
        screenShareActive: source === MediaDevices.CAMERA && this._isScreenShareActive()
      })
    );
  }

  _isAuthExpiredRequestError(error) {
    const message = `${error?.message || error?.reason || error?.data?.message || ""}`.toLowerCase();

    return message.includes("jwt expired") || message.includes("token expired");
  }

  async _refreshHubPermissionsToken() {
    const app = typeof window !== "undefined" ? window.APP : null;

    await app?.hubChannel?.fetchPermissions?.();
  }

  async refreshAuthToken({ fetchPermissions = false } = {}) {
    const app = typeof window !== "undefined" ? window.APP : null;
    const hubChannel = app?.hubChannel;

    logRtcSfuRecovery("auth-refresh:start", {
      roomId: this._roomId,
      fetchPermissions,
      hasHubChannel: Boolean(hubChannel),
      hasProtooRequest: Boolean(this._protoo?.request),
      protooConnected: Boolean(this._protoo?.connected),
      dialogJoined: Boolean(this._dialogJoined)
    });

    if (!hubChannel || !this._protoo?.request) {
      logRtcSfuRecovery("auth-refresh:skipped", {
        roomId: this._roomId,
        reason: !hubChannel ? "missing-hub-channel" : "missing-protoo-request"
      });
      return false;
    }

    if (fetchPermissions && typeof hubChannel.fetchPermissions === "function") {
      logRtcSfuRecovery("auth-refresh:fetch-permissions:start", { roomId: this._roomId });
      try {
        await hubChannel.fetchPermissions();
        logRtcSfuRecovery("auth-refresh:fetch-permissions:done", { roomId: this._roomId });
      } catch (err) {
        logRtcSfuRecovery("auth-refresh:fetch-permissions:error", {
          roomId: this._roomId,
          error: summarizeRtcSfuError(err)
        });
        throw err;
      }
    }

    const token = hubChannel.token;
    const tokenChanged = Boolean(token && token !== this._dialogAuthToken);

    logRtcSfuRecovery("auth-refresh:token-state", {
      roomId: this._roomId,
      hasToken: Boolean(token),
      tokenChanged
    });

    if (!token || !tokenChanged) {
      logRtcSfuRecovery("auth-refresh:skipped", {
        roomId: this._roomId,
        reason: !token ? "missing-token" : "token-unchanged"
      });
      return false;
    }

    logRtcSfuRecovery("auth-refresh:request:start", {
      roomId: this._roomId,
      protooConnected: Boolean(this._protoo?.connected),
      dialogJoined: Boolean(this._dialogJoined)
    });

    try {
      await this._requestProtooWithReconnectRetry("refreshAuthToken", { token });
    } catch (err) {
      logRtcSfuRecovery("auth-refresh:request:error", { roomId: this._roomId, error: summarizeRtcSfuError(err) });
      throw err;
    }

    this._dialogAuthToken = token;
    logRtcSfuRecovery("auth-refresh:request:done", { roomId: this._roomId });

    return true;
  }

  async _recoverAuthExpiredSignaling() {
    if (!this._dialogEndpoint) {
      return false;
    }

    if (!this._authRecoveryPromise) {
      this._authRecoveryPromise = (async () => {
        await this._refreshHubPermissionsToken();
        await this._connectToEndpoint(this._dialogEndpoint, { suppressRecovery: true });
      })().finally(() => {
        this._authRecoveryPromise = null;
      });
    }

    await this._authRecoveryPromise;
    return true;
  }

  _isSignalingReady() {
    return Boolean(this._protoo?.connected && this._dialogJoined);
  }

  _markSignalingUnavailable(reason) {
    if (!this._signalingReadyPromise) {
      this._signalingReadyPromise = new Promise(resolve => {
        this._resolveSignalingReady = resolve;
      });
    }

    this._rejectPendingProtooRequests(reason);
  }

  _markSignalingReady(ready = true) {
    const resolve = this._resolveSignalingReady;
    this._signalingReadyPromise = null;
    this._resolveSignalingReady = null;
    resolve?.(ready);
  }

  _rejectPendingProtooRequests(reason) {
    for (const pendingRequest of this._pendingProtooRequests) {
      pendingRequest.reject(createProtooDisconnectedError(pendingRequest.type, reason));
    }
    this._pendingProtooRequests.clear();
  }

  async _waitForSignalingReady(type) {
    if (this._isSignalingReady()) {
      return;
    }

    if (this._signalingReadyPromise) {
      logRtcSfuRecovery("protoo-request:wait-for-ready", {
        roomId: this._roomId,
        type,
        protooConnected: Boolean(this._protoo?.connected),
        dialogJoined: Boolean(this._dialogJoined)
      });

      await this._signalingReadyPromise;
    }

    if (this._isSignalingReady()) {
      return;
    }

    throw createProtooDisconnectedError(type, this._protoo?.connected ? "not-joined" : "disconnected");
  }

  async _requestProtoo(type, payload) {
    await this._waitForSignalingReady(type);

    if (typeof this._protoo?.request !== "function") {
      throw createProtooDisconnectedError(type, "missing-peer");
    }

    const timeoutMs = getRtcSfuProtooRequestTimeoutMs();
    const pendingRequest = { type, reject: null };
    const disconnectPromise = new Promise((_, reject) => {
      pendingRequest.reject = reject;
    });
    this._pendingProtooRequests.add(pendingRequest);

    let timeoutId;

    try {
      const requestPromises = [this._protoo.request(type, payload), disconnectPromise];

      if (timeoutMs) {
        requestPromises.push(
          new Promise((_, reject) => {
            timeoutId = setTimeout(() => reject(createProtooRequestTimeoutError(type, timeoutMs)), timeoutMs);
          })
        );
      }

      return await Promise.race(requestPromises);
    } catch (err) {
      if (err?.code === "RTC_SFU_PROTOO_REQUEST_TIMEOUT") {
        logRtcSfuRecovery("protoo-request:timeout", {
          roomId: this._roomId,
          type,
          timeoutMs,
          protooConnected: Boolean(this._protoo?.connected),
          dialogJoined: Boolean(this._dialogJoined)
        });
        await this._recoverFromTimedOutProtooRequest(type, err);
      }

      throw err;
    } finally {
      clearTimeout(timeoutId);
      this._pendingProtooRequests.delete(pendingRequest);
    }
  }

  async _requestProtooWithReconnectRetry(type, payload) {
    try {
      return await this._requestProtoo(type, payload);
    } catch (err) {
      if (err?.code !== "RTC_SFU_PROTOO_DISCONNECTED") {
        throw err;
      }

      logRtcSfuRecovery("protoo-request:retry-after-reconnect", {
        roomId: this._roomId,
        type,
        reason: err.reason
      });

      await this._waitForSignalingReady(type);
      return this._requestProtoo(type, payload);
    }
  }

  async _recoverFromTimedOutProtooRequest(type, requestError) {
    if (!this._dialogEndpoint) {
      logRtcSfuRecovery("protoo-request:timeout-recovery-skipped", {
        roomId: this._roomId,
        type,
        reason: "missing-endpoint",
        error: summarizeRtcSfuError(requestError)
      });
      return;
    }

    if (this._disconnecting || this._recovering) {
      logRtcSfuRecovery("protoo-request:timeout-recovery-skipped", {
        roomId: this._roomId,
        type,
        reason: this._disconnecting ? "disconnecting" : "already-recovering",
        error: summarizeRtcSfuError(requestError)
      });
      return;
    }

    logRtcSfuRecovery("protoo-request:timeout-recovery-start", {
      roomId: this._roomId,
      type,
      endpoint: summarizeRtcSfuEndpoint(this._dialogEndpoint)
    });
    await this._recoverFromUnexpectedClose();
  }

  async _requestWithAuthRecovery(type, payload) {
    logRtcSfuRecovery("protoo-request:start", {
      roomId: this._roomId,
      type,
      protooConnected: Boolean(this._protoo?.connected),
      dialogJoined: Boolean(this._dialogJoined),
      hasSendTransport: Boolean(this._sendTransport),
      sendTransportState: this._sendTransport?.connectionState,
      hasRecvTransport: Boolean(this._recvTransport),
      recvTransportState: this._recvTransport?.connectionState,
      payload: summarizeDialogRequestPayload(payload)
    });

    try {
      const response = await this._requestProtooWithReconnectRetry(type, payload);
      logRtcSfuRecovery("protoo-request:success", {
        roomId: this._roomId,
        type,
        protooConnected: Boolean(this._protoo?.connected),
        dialogJoined: Boolean(this._dialogJoined),
        response: summarizeDialogResponse(response)
      });

      return response;
    } catch (err) {
      if (!this._isAuthExpiredRequestError(err) || !(await this._recoverAuthExpiredSignaling())) {
        logRtcSfuRecovery("protoo-request:error", {
          roomId: this._roomId,
          type,
          protooConnected: Boolean(this._protoo?.connected),
          dialogJoined: Boolean(this._dialogJoined),
          payload: summarizeDialogRequestPayload(payload),
          error: summarizeRtcSfuError(err)
        });
        throw err;
      }

      logRtcSfuRecovery("protoo-request:retry-after-auth-recovery", { roomId: this._roomId, type });
      try {
        const response = await this._requestProtooWithReconnectRetry(type, payload);
        logRtcSfuRecovery("protoo-request:success-after-auth-recovery", {
          roomId: this._roomId,
          type,
          response: summarizeDialogResponse(response)
        });

        return response;
      } catch (retryErr) {
        logRtcSfuRecovery("protoo-request:retry-error", {
          roomId: this._roomId,
          type,
          payload: summarizeDialogRequestPayload(payload),
          error: summarizeRtcSfuError(retryErr)
        });
        throw retryErr;
      }
    }
  }

  async _connectToEndpoint(endpoint, { suppressRecovery = false } = {}) {
    this._markSignalingUnavailable("reconnecting");
    logRtcSfuRecovery("endpoint-connect:start", {
      roomId: this._roomId,
      endpoint: summarizeRtcSfuEndpoint(endpoint),
      suppressRecovery,
      hasResolveRoomSession: Boolean(this._resolveRoomSession)
    });

    if (this._resolveRoomSession && (endpoint?.usingRtcSfuOrchestrator || endpoint?.allocation)) {
      let roomSession;

      try {
        roomSession = await this._resolveRoomSession();
      } catch (err) {
        logRtcSfuRecovery("room-session:refresh-error", {
          roomId: this._roomId,
          error: summarizeRtcSfuError(err)
        });
        throw err;
      }

      this._allocationGrant = roomSession?.allocationGrant || this._allocationGrant;
      this._peerMode = roomSession?.peerMode || this._peerMode;
      this._roomSessionId = roomSession?.roomSessionId || this._roomSessionId;
      this._reconnectToken = roomSession?.reconnectToken || this._reconnectToken;
      this._roomResourceLimits = roomSession?.roomResourceLimits || this._roomResourceLimits;
      this._roomMediaQuality = normalizeDialogMediaQualityState(roomSession?.roomMediaQuality, this._roomMediaQuality);

      logRtcSfuRecovery("room-session:refreshed", {
        roomId: this._roomId,
        session: summarizeRtcSfuRoomSession(roomSession)
      });
    }

    this.cleanUpLocalState();
    if (this._protoo) {
      this._protoo.removeAllListeners();
      this._protoo.close?.();
    }

    return this.connect({
      serverUrl: endpoint.serverUrl,
      roomId: this._roomId,
      serverParams: {
        host: endpoint.host,
        port: endpoint.port,
        turn: endpoint.turn,
        iceServers: endpoint.iceServers || endpoint.ice_servers,
        allocation: endpoint.allocation
      },
      scene: this.scene,
      clientId: this._clientId,
      forceTcp: this._forceTcp,
      forceTurn: this._forceTurn,
      iceTransportPolicy: this._iceTransportPolicy,
      dialogEndpoint: endpoint,
      resolveDialogEndpoint: this._resolveDialogEndpoint,
      resolveRoomSession: this._resolveRoomSession,
      allocationGrant: this._allocationGrant,
      peerMode: this._peerMode,
      roomSessionId: this._roomSessionId,
      reconnectToken: this._reconnectToken,
      roomResourceLimits: this._roomResourceLimits,
      roomMediaQuality: this._roomMediaQuality,
      suppressRecovery
    });
  }

  async _recoverFromUnexpectedClose() {
    this._markSignalingUnavailable("recovering");
    logRtcSfuRecovery("recovery:start", {
      roomId: this._roomId,
      endpoint: summarizeRtcSfuEndpoint(this._dialogEndpoint),
      hasResolveDialogEndpoint: Boolean(this._resolveDialogEndpoint),
      isRtcSfuOrchestratorEndpoint: this._isRtcSfuOrchestratorEndpoint()
    });

    if (!this._isRtcSfuOrchestratorEndpoint() || !this._resolveDialogEndpoint) {
      logRtcSfuRecovery("recovery:legacy-host-retry", {
        roomId: this._roomId,
        reason: !this._isRtcSfuOrchestratorEndpoint() ? "not-orchestrator-endpoint" : "missing-endpoint-resolver"
      });
      await this._retryConnectWithNewHost();
      return;
    }

    this._recovering = true;
    this.cleanUpLocalState();

    try {
      const { endpoint } = await recoverRtcSfuConnection({
        endpoint: this._dialogEndpoint,
        connect: endpoint => this._connectToEndpoint(endpoint, { suppressRecovery: true }),
        resolveFreshEndpoint: ({ onStateChange }) => this._resolveDialogEndpoint({ onStateChange }),
        onStateChange: state => this._emitConnectionState(state),
        options: this._recoveryOptions
      });

      this._dialogEndpoint = endpoint;
      logRtcSfuRecovery("recovery:connected", {
        roomId: this._roomId,
        endpoint: summarizeRtcSfuEndpoint(endpoint)
      });
    } catch (err) {
      logRtcSfuRecovery("recovery:error", {
        roomId: this._roomId,
        endpoint: summarizeRtcSfuEndpoint(this._dialogEndpoint),
        error: summarizeRtcSfuError(err)
      });
      this.emitRTCEvent("error", "Signaling", () => `Recovery failed: ${err}`);
      this.emit(DIALOG_CONNECTION_ERROR_FATAL, { error: err, duringRecovery: true });
    } finally {
      this._recovering = false;
      logRtcSfuRecovery("recovery:done", { roomId: this._roomId });
    }
  }

  async _retryConnectWithNewHost() {
    logRtcSfuRecovery("legacy-host-retry:start", { roomId: this._roomId, currentServerUrl: this._serverUrl });
    this.cleanUpLocalState();
    this._protoo.removeAllListeners();
    const serverParams = await APP.hubChannel.getHost();
    const { host, port } = serverParams;
    const newServerUrl = `wss://${host}:${port}`;
    if (this._serverUrl === newServerUrl) {
      logRtcSfuRecovery("legacy-host-retry:same-host", { roomId: this._roomId, newServerUrl });
      this.emit(DIALOG_CONNECTION_ERROR_FATAL, { duringRecovery: false });
      return;
    }

    logRtcSfuRecovery("legacy-host-retry:new-host", { roomId: this._roomId, newServerUrl });
    await this.connect({
      serverUrl: newServerUrl,
      roomId: this._roomId,
      serverParams,
      scene: this.scene,
      clientId: this._clientId,
      forceTcp: this._forceTcp,
      forceTurn: this._forceTurn,
      iceTransportPolicy: this._iceTransportPolicy,
      dialogEndpoint: {
        host,
        port,
        turn: serverParams.turn,
        serverUrl: newServerUrl,
        usingOverride: false
      },
      allocationGrant: this._allocationGrant,
      peerMode: this._peerMode,
      roomSessionId: this._roomSessionId,
      reconnectToken: this._reconnectToken,
      roomMediaQuality: this._roomMediaQuality
    });
  }

  closePeer(peerId) {
    const pendingMediaRequests = this._pendingMediaRequests.get(peerId);

    if (pendingMediaRequests) {
      const msg = "The user disconnected before the media stream was resolved.";
      info(msg);

      Object.values(pendingMediaRequests).forEach(request => request.resolve(null));

      this._pendingMediaRequests.delete(peerId);
    }
  }

  resolvePendingMediaRequestForTrack(clientId, track, appData = {}) {
    const requests = this._pendingMediaRequests.get(clientId);

    if (!requests) {
      return;
    }

    const keys = [track.kind];

    if (track.kind === "video") {
      keys.unshift(getMediaRequestKey(track.kind, getVideoSourceForTrack(track, appData)));
    }

    for (const key of keys) {
      if (requests[key]) {
        const resolve = requests[key].resolve;
        delete requests[key];
        resolve(new MediaStream([track]));
      }
    }

    if (Object.keys(requests).length === 0) {
      this._pendingMediaRequests.delete(clientId);
    }
  }

  removeConsumer(consumerId) {
    this.emitRTCEvent("info", "RTC", () => `Consumer removed: ${consumerId}`);
    this._consumers.delete(consumerId);
  }

  getMediaStream(clientId, kind = "audio", source = null) {
    let track;

    if (this._clientId === clientId) {
      if (kind === "audio" && this._micProducer) {
        track = this._micProducer.track;
      } else if (kind === "video") {
        if (source === MediaDevices.SCREEN && this._shareProducer && !this._shareProducer.closed) {
          track = this._shareProducer.track;
        } else if (source === MediaDevices.CAMERA && this._cameraProducer && !this._cameraProducer.closed) {
          track = this._cameraProducer.track;
        } else if (!source && this._cameraProducer && !this._cameraProducer.closed) {
          track = this._cameraProducer.track;
        } else if (!source && this._shareProducer && !this._shareProducer.closed) {
          track = this._shareProducer.track;
        }
      }
    } else {
      let fallbackShareTrack = null;

      this._consumers.forEach(consumer => {
        if (
          !consumer.closed &&
          consumer.appData.peerId === clientId &&
          kind == consumer.track.kind &&
          matchesRequestedMediaSource(consumer.track, consumer.appData, source)
        ) {
          const isCameraTrack =
            kind !== "video" || getVideoSourceForTrack(consumer.track, consumer.appData) === MediaDevices.CAMERA;

          if (source || isCameraTrack) {
            track = track || consumer.track;
          } else {
            fallbackShareTrack = fallbackShareTrack || consumer.track;
          }
        }
      });

      track = track || fallbackShareTrack;
    }

    if (track) {
      debug(`Already had ${getMediaRequestKey(kind, source)} for ${clientId}`);
      return Promise.resolve(new MediaStream([track]));
    } else {
      debug(`Waiting on ${getMediaRequestKey(kind, source)} for ${clientId}`);
      if (!this._pendingMediaRequests.has(clientId)) {
        this._pendingMediaRequests.set(clientId, {});
      }

      const requests = this._pendingMediaRequests.get(clientId);
      const requestKey = getMediaRequestKey(kind, source);
      const promise = new Promise((resolve, reject) => (requests[requestKey] = { resolve, reject }));
      requests[requestKey].promise = promise;
      promise.catch(e => {
        this.emitRTCEvent("error", "Adapter", () => `getMediaStream error: ${e}`);
      });
      return promise;
    }
  }

  async createSendTransport(iceServers) {
    // Create mediasoup Transport for sending (unless we don't want to produce).
    const sendTransportInfo = await this._protoo.request("createWebRtcTransport", {
      producing: true,
      consuming: false,
      sctpCapabilities: undefined
    });

    this._sendTransport = this._mediasoupDevice.createSendTransport({
      id: sendTransportInfo.id,
      iceParameters: sendTransportInfo.iceParameters,
      iceCandidates: sendTransportInfo.iceCandidates,
      dtlsParameters: sendTransportInfo.dtlsParameters,
      sctpParameters: sendTransportInfo.sctpParameters,
      iceServers,
      iceTransportPolicy: this._iceTransportPolicy,
      proprietaryConstraints: PC_PROPRIETARY_CONSTRAINTS
    });

    this._sendTransport.on(
      "connect",
      (
        { dtlsParameters },
        callback,
        errback // eslint-disable-line no-shadow
      ) => {
        this.emitRTCEvent("info", "RTC", () => `Send transport [connect]`);
        this._sendTransport.observer.on("close", () => {
          this.emitRTCEvent("info", "RTC", () => `Send transport [close]`);
        });
        this._sendTransport.observer.on("newproducer", producer => {
          this.emitRTCEvent("info", "RTC", () => `Send transport [newproducer]: ${producer.id}`);
        });
        this._sendTransport.observer.on("newconsumer", consumer => {
          this.emitRTCEvent("info", "RTC", () => `Send transport [newconsumer]: ${consumer.id}`);
        });

        this._protoo
          .request("connectWebRtcTransport", {
            transportId: this._sendTransport.id,
            dtlsParameters
          })
          .then(callback)
          .catch(errback);
      }
    );

    this._sendTransport.on("connectionstatechange", connectionState => {
      logRtcSfuRecovery("transport:send-state", {
        roomId: this._roomId,
        state: connectionState,
        transportId: this._sendTransport?.id,
        protooConnected: Boolean(this._protoo?.connected),
        dialogJoined: Boolean(this._dialogJoined)
      });
      let level = "info";
      if (connectionState === "failed" || connectionState === "disconnected") {
        level = "error";
      }
      this.emitRTCEvent(level, "RTC", () => `Send transport [connectionstatechange]: ${connectionState}`);

      this.checkSendIceStatus(connectionState);
    });

    this._sendTransport.on("produce", async ({ kind, rtpParameters, appData }, callback, errback) => {
      this.emitRTCEvent("info", "RTC", () => `Send transport [produce]: ${kind}`);
      try {
        // eslint-disable-next-line no-shadow
        const { id } = await this._protoo.request("produce", {
          transportId: this._sendTransport.id,
          kind,
          rtpParameters,
          appData
        });

        callback({ id });
      } catch (error) {
        this.emitRTCEvent("error", "Signaling", () => `[produce] error: ${error}`);
        errback(error);
      }
    });
  }

  async closeSendTransport() {
    const closeLocalProducer = producer => {
      producer.close();
      this._protoo?.connected && this._protoo?.request("closeProducer", { producerId: producer.id });
    };

    if (this._micProducer) {
      closeLocalProducer(this._micProducer);
      this._micProducer = null;
    }

    if (this._cameraProducer) {
      closeLocalProducer(this._cameraProducer);
      this._cameraProducer = null;
    }

    if (this._shareProducer) {
      closeLocalProducer(this._shareProducer);
      this._shareProducer = null;
    }

    // TODO: If _sendTransport is falsey then return
    const transportId = this._sendTransport?.id;
    if (this._sendTransport && !this._sendTransport._closed) {
      this._sendTransport.close();
      this._sendTransport = null;
    }

    if (this._protoo?.connected) {
      try {
        await this._protoo.request("closeWebRtcTransport", { transportId });
      } catch (err) {
        error(err);
      }
    }
  }

  async createRecvTransport(iceServers) {
    // Create mediasoup Transport for sending (unless we don't want to consume).
    const recvTransportInfo = await this._protoo.request("createWebRtcTransport", {
      producing: false,
      consuming: true,
      sctpCapabilities: undefined
    });

    this._recvTransport = this._mediasoupDevice.createRecvTransport({
      id: recvTransportInfo.id,
      iceParameters: recvTransportInfo.iceParameters,
      iceCandidates: recvTransportInfo.iceCandidates,
      dtlsParameters: recvTransportInfo.dtlsParameters,
      sctpParameters: recvTransportInfo.sctpParameters,
      iceServers,
      iceTransportPolicy: this._iceTransportPolicy
    });

    this._recvTransport.on(
      "connect",
      (
        { dtlsParameters },
        callback,
        errback // eslint-disable-line no-shadow
      ) => {
        this.emitRTCEvent("info", "RTC", () => `Receive transport [connect]`);
        this._recvTransport.observer.on("close", () => {
          this.emitRTCEvent("info", "RTC", () => `Receive transport [close]`);
        });
        this._recvTransport.observer.on("newproducer", producer => {
          this.emitRTCEvent("info", "RTC", () => `Receive transport [newproducer]: ${producer.id}`);
        });
        this._recvTransport.observer.on("newconsumer", consumer => {
          this.emitRTCEvent("info", "RTC", () => `Receive transport [newconsumer]: ${consumer.id}`);
        });

        this._protoo
          .request("connectWebRtcTransport", {
            transportId: this._recvTransport.id,
            dtlsParameters
          })
          .then(callback)
          .catch(errback);
      }
    );

    this._recvTransport.on("connectionstatechange", connectionState => {
      logRtcSfuRecovery("transport:recv-state", {
        roomId: this._roomId,
        state: connectionState,
        transportId: this._recvTransport?.id,
        protooConnected: Boolean(this._protoo?.connected),
        dialogJoined: Boolean(this._dialogJoined)
      });
      let level = "info";
      if (connectionState === "failed" || connectionState === "disconnected") {
        level = "error";
      }
      this.emitRTCEvent(level, "RTC", () => `Receive transport [connectionstatechange]: ${connectionState}`);

      this.checkRecvIceStatus(connectionState);
    });
  }

  async closeRecvTransport() {
    const transportId = this._recvTransport?.id;
    if (this._recvTransport && !this._recvTransport._closed) {
      this._recvTransport.close();
      this._recvTransport = null;
    }
    if (this._protoo?.connected) {
      try {
        await this._protoo.request("closeWebRtcTransport", { transportId });
      } catch (err) {
        error(err);
      }
    }
  }

  async _joinRoom() {
    debug("_joinRoom()");
    logRtcSfuRecovery("join-room:start", {
      roomId: this._roomId,
      endpoint: summarizeRtcSfuEndpoint(this._dialogEndpoint),
      hasAllocationGrant: Boolean(this._allocationGrant),
      peerMode: this._peerMode,
      roomSessionId: this._roomSessionId,
      hasLocalMediaStream: Boolean(this._localMediaStream)
    });

    this._mediasoupDevice = new mediasoupClient.Device({});

    const routerRtpCapabilities = await this._protoo.request("getRouterRtpCapabilities");

    await this._mediasoupDevice.load({ routerRtpCapabilities });

    const { host, port, turn, iceServers, ice_servers } = this._serverParams;
    const resolvedIceServers = this.getIceServers(host, port, turn, iceServers || ice_servers);

    await this.createSendTransport(resolvedIceServers);
    await this.createRecvTransport(resolvedIceServers);

    const token = APP.hubChannel.token;
    const senderUserId = resolveRoomUserId({ store: APP.store });

    const joinResponse = await this._protoo.request("join", {
      displayName: this._getDisplayName(),
      device: this._device,
      rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
      sctpCapabilities: this._useDataChannel ? this._mediasoupDevice.sctpCapabilities : undefined,
      token,
      allocationGrant: this._allocationGrant,
      peerMode: this._peerMode,
      roomSessionId: this._roomSessionId,
      roomMediaQuality: this._roomMediaQuality,
      senderUserId
    });
    if (joinResponse?.roomMediaQuality) {
      this.handleRoomMediaQualityChanged(joinResponse.roomMediaQuality, { apply: false });
    }
    this._dialogAuthToken = token;
    logRtcSfuRecovery("join-room:joined", {
      roomId: this._roomId,
      sendTransportId: this._sendTransport?.id,
      recvTransportId: this._recvTransport?.id,
      hasLocalMediaStream: Boolean(this._localMediaStream)
    });

    if (this._localMediaStream) {
      // TODO: Refactor to be "Create producers"
      await this.setLocalMediaStream(this._localMediaStream);
    }
  }

  async setLocalMediaStream(stream) {
    this._localMediaStream = stream;
    const tracks = stream.getTracks();
    const willHaveScreenShare = tracks.some(
      track => track.kind === "video" && track._hubs_contentHint === MediaDevices.SCREEN
    );

    logRtcSfuRecovery("local-media:set", {
      roomId: this._roomId,
      hasSendTransport: Boolean(this._sendTransport),
      sendTransportState: this._sendTransport?.connectionState,
      ...summarizeLocalMediaTracks(tracks),
      tracks: tracks.map(summarizeRtcSfuTrack)
    });

    if (!this._sendTransport) {
      logRtcSfuRecovery("local-media:set-deferred", {
        roomId: this._roomId,
        reason: "missing-send-transport",
        trackCount: tracks.length
      });
      return;
    }
    this.emitRTCEvent("info", "RTC", () => `Creating missing producers`);
    let sawAudio = false;
    let sawCamera = false;
    let sawShare = false;

    for (const track of tracks) {
      if (track.kind === "audio") {
        sawAudio = true;

        // TODO multiple audio tracks?
        if (this._micProducer) {
          if (this._micProducer.track !== track) {
            this._micProducer.track.stop();
            this._micProducer.replaceTrack(track);
          }
        } else {
          // stopTracks = false because otherwise the track will end during a temporary disconnect
          logRtcSfuRecovery("local-media:producer-start", {
            roomId: this._roomId,
            source: "mic",
            sendTransportState: this._sendTransport?.connectionState,
            track: summarizeRtcSfuTrack(track)
          });
          const micShouldBeActive = this._micShouldBeEnabled && this.canAcquireRoomResource(RoomResourceIds.MICROPHONE);
          if (this._micShouldBeEnabled && !micShouldBeActive) {
            this._micShouldBeEnabled = false;
          }

          try {
            this._micProducer = await this._sendTransport.produce({
              track,
              pause: !micShouldBeActive,
              stopTracks: false,
              codecOptions: {
                opusStereo: false,
                opusDtx: true,
                opusFec: true,
                opusMaxAverageBitrate: 32000
              },
              appData: {
                source: "mic",
                processing: APP.mediaDevicesManager?.micAudioProcessingLabel || MIC_AUDIO_PROCESSING_LABELS.NATIVE
              },
              zeroRtpOnPause: true,
              disableTrackOnPause: true
            });
            logRtcSfuRecovery("local-media:producer-done", {
              roomId: this._roomId,
              source: "mic",
              producerId: this._micProducer?.id
            });
          } catch (err) {
            logRtcSfuRecovery("local-media:producer-error", {
              roomId: this._roomId,
              source: "mic",
              sendTransportState: this._sendTransport?.connectionState,
              error: summarizeRtcSfuError(err)
            });
            throw err;
          }

          this._micProducer.on("transportclose", () => {
            this.emitRTCEvent("info", "RTC", () => `Mic transport closed`);
            this._micProducer = null;
          });

          this.emit("mic-state-changed", { enabled: this.isMicEnabled });
        }
      } else if (track.kind === "video") {
        if (track._hubs_contentHint === MediaDevices.SCREEN) {
          sawShare = true;

          if (!this._shareProducer || this._shareProducer.closed) {
            await this.enableShare(track);
          } else if (this._shareProducer.track !== track) {
            await this.disableShare();
            await this.enableShare(track);
          }
        } else if (track._hubs_contentHint === MediaDevices.CAMERA) {
          sawCamera = true;

          if (!this._cameraProducer || this._cameraProducer.closed) {
            await this.enableCamera(track, { screenShareActive: willHaveScreenShare });
          } else if (this._cameraProducer.track !== track) {
            await this.disableCamera();
            await this.enableCamera(track, { screenShareActive: willHaveScreenShare });
          }
        }
      }

      this.resolvePendingMediaRequestForTrack(this._clientId, track);
    }

    if (!sawAudio && this._micProducer) {
      this._protoo.request("closeProducer", { producerId: this._micProducer.id });
      this._micProducer.close();
      this._micProducer = null;
    }
    if (!sawCamera) {
      await this.disableCamera();
    }
    if (!sawShare) {
      await this.disableShare();
    }

    logRtcSfuRecovery("local-media:producers-updated", {
      roomId: this._roomId,
      sawAudio,
      sawCamera,
      sawShare,
      hasMicProducer: Boolean(this._micProducer),
      hasCameraProducer: Boolean(this._cameraProducer),
      hasShareProducer: Boolean(this._shareProducer)
    });
  }

  async enableCamera(track, { screenShareActive = this._isScreenShareActive() } = {}) {
    const { codecOptions, encodings } = cameraProducerOptionsForMediaQuality(this._roomMediaQuality, {
      screenShareActive
    });

    logRtcSfuRecovery("local-media:producer-start", {
      roomId: this._roomId,
      source: MediaDevices.CAMERA,
      sendTransportState: this._sendTransport?.connectionState,
      track: summarizeRtcSfuTrack(track)
    });

    // stopTracks = false because otherwise the track will end during a temporary disconnect
    try {
      this._cameraProducer = await this._sendTransport.produce({
        track,
        stopTracks: false,
        codecOptions,
        encodings,
        zeroRtpOnPause: true,
        disableTrackOnPause: true
      });
      logRtcSfuRecovery("local-media:producer-done", {
        roomId: this._roomId,
        source: MediaDevices.CAMERA,
        producerId: this._cameraProducer?.id
      });
    } catch (err) {
      logRtcSfuRecovery("local-media:producer-error", {
        roomId: this._roomId,
        source: MediaDevices.CAMERA,
        sendTransportState: this._sendTransport?.connectionState,
        error: summarizeRtcSfuError(err)
      });
      throw err;
    }

    this._cameraProducer.on("transportclose", () => {
      this.emitRTCEvent("info", "RTC", () => `Camera transport closed`);
      this.disableCamera();
    });
    this._cameraProducer.observer.on("trackended", () => {
      this.emitRTCEvent("info", "RTC", () => `Camera track ended`);
      this.disableCamera();
    });
  }

  async disableCamera() {
    if (!this._cameraProducer) return;

    this._cameraProducer.close();

    try {
      if (!this._sendTransport.closed) {
        await this._protoo.request("closeProducer", { producerId: this._cameraProducer.id });
      }
    } catch (error) {
      //
    }

    this._cameraProducer = null;
  }

  async enableShare(track) {
    const { codecOptions, encodings } = screenProducerOptionsForMediaQuality(this._roomMediaQuality);

    applyScreenShareContentHint(track);

    logRtcSfuRecovery("local-media:producer-start", {
      roomId: this._roomId,
      source: MediaDevices.SCREEN,
      sendTransportState: this._sendTransport?.connectionState,
      track: summarizeRtcSfuTrack(track)
    });

    // stopTracks = false because otherwise the track will end during a temporary disconnect
    try {
      this._shareProducer = await this._sendTransport.produce({
        track,
        stopTracks: false,
        codecOptions,
        encodings,
        zeroRtpOnPause: true,
        disableTrackOnPause: true,
        appData: {
          share: true
        }
      });
      logRtcSfuRecovery("local-media:producer-done", {
        roomId: this._roomId,
        source: MediaDevices.SCREEN,
        producerId: this._shareProducer?.id
      });
      await this._applyProducerMediaQuality(this._cameraProducer, MediaDevices.CAMERA);
    } catch (err) {
      logRtcSfuRecovery("local-media:producer-error", {
        roomId: this._roomId,
        source: MediaDevices.SCREEN,
        sendTransportState: this._sendTransport?.connectionState,
        error: summarizeRtcSfuError(err)
      });
      throw err;
    }

    this._shareProducer.on("transportclose", () => {
      this.emitRTCEvent("info", "RTC", () => `Desktop Share transport closed`);
      this.disableShare();
    });
    this._shareProducer.observer.on("trackended", () => {
      this.emitRTCEvent("info", "RTC", () => `Desktop Share transport track ended`);
      this.disableShare();
    });
  }

  async disableShare() {
    if (!this._shareProducer) return;

    this._shareProducer.close();

    try {
      if (!this._sendTransport.closed) {
        await this._protoo.request("closeProducer", { producerId: this._shareProducer.id });
      }
    } catch (error) {
      //
    }

    this._shareProducer = null;
    await this._applyProducerMediaQuality(this._cameraProducer, MediaDevices.CAMERA);
  }

  toggleMicrophone() {
    if (this._micProducer ? this.isMicEnabled : this._micShouldBeEnabled) {
      this.enableMicrophone(false);
    } else {
      this.enableMicrophone(true);
    }
  }

  enableMicrophone(enabled) {
    if (enabled && !this.isMicEnabled && !this.canAcquireRoomResource(RoomResourceIds.MICROPHONE)) {
      return false;
    }

    this._micShouldBeEnabled = enabled;

    if (!this._micProducer) {
      this.emit("mic-state-changed", { enabled });
      return true;
    }

    if (enabled && !this.isMicEnabled) {
      this._micProducer.resume();
      this._protoo.request("resumeProducer", { producerId: this._micProducer.id });
    } else if (!enabled && this.isMicEnabled) {
      this._micProducer.pause();
      this._protoo.request("pauseProducer", { producerId: this._micProducer.id });
    }
    this.emit("mic-state-changed", { enabled: this.isMicEnabled });
    return true;
  }

  async moderatePeerMedia(peerId, source) {
    if (source !== MediaDevices.CAMERA && source !== MediaDevices.SCREEN) {
      return;
    }

    await this.refreshAuthToken();

    return this._requestWithAuthRecovery("moderatePeerMedia", { peerId, source });
  }

  async sendChatMessage({ text, clientMessageId, language, metadata } = {}) {
    logRtcSfuRecovery("chat:send-public:start", {
      roomId: this._roomId,
      protooConnected: Boolean(this._protoo?.connected),
      dialogJoined: Boolean(this._dialogJoined),
      sendTransportState: this._sendTransport?.connectionState,
      payload: summarizeDialogRequestPayload({ text, clientMessageId, language, metadata })
    });
    await this.refreshAuthToken();
    logRtcSfuRecovery("chat:send-public:after-auth", {
      roomId: this._roomId,
      protooConnected: Boolean(this._protoo?.connected),
      dialogJoined: Boolean(this._dialogJoined)
    });

    return this._requestWithAuthRecovery("sendChatMessage", { text, clientMessageId, language, metadata });
  }

  async sendPrivateChatMessage({
    text,
    recipientSessionId,
    recipientName,
    clientMessageId,
    senderUserId,
    language,
    metadata
  } = {}) {
    logRtcSfuRecovery("chat:send-private:start", {
      roomId: this._roomId,
      protooConnected: Boolean(this._protoo?.connected),
      dialogJoined: Boolean(this._dialogJoined),
      sendTransportState: this._sendTransport?.connectionState,
      payload: summarizeDialogRequestPayload({ text, recipientSessionId, clientMessageId, language, metadata })
    });
    await this.refreshAuthToken();
    logRtcSfuRecovery("chat:send-private:after-auth", {
      roomId: this._roomId,
      protooConnected: Boolean(this._protoo?.connected),
      dialogJoined: Boolean(this._dialogJoined)
    });

    return this._requestWithAuthRecovery("sendPrivateChatMessage", {
      text,
      recipientSessionId,
      recipientName,
      clientMessageId,
      senderUserId,
      language,
      metadata
    });
  }

  handlePeerMediaModerated({ source } = {}) {
    if (source === MediaDevices.CAMERA || source === MediaDevices.SCREEN) {
      this.scene?.emit?.(MediaDevicesEvents.VIDEO_SHARE_ENDED, { source });
    }
  }

  get isMicEnabled() {
    return !!this._micProducer && !this._micProducer.paused;
  }

  get isMicDesiredEnabled() {
    return !!this._micShouldBeEnabled;
  }

  cleanUpLocalState() {
    logRtcSfuRecovery("local-state:cleanup", {
      roomId: this._roomId,
      sendTransportId: this._sendTransport?.id,
      recvTransportId: this._recvTransport?.id,
      hasMicProducer: Boolean(this._micProducer),
      hasCameraProducer: Boolean(this._cameraProducer),
      hasShareProducer: Boolean(this._shareProducer)
    });
    this._sendTransport && this._sendTransport.close();
    this._sendTransport = null;
    this._recvTransport && this._recvTransport.close();
    this._recvTransport = null;
    this._micProducer = null;
    this._shareProducer = null;
    this._cameraProducer = null;
  }

  disconnect() {
    debug("disconnect()");
    this._dialogJoined = false;
    logRtcSfuRecovery("dialog-disconnect", {
      roomId: this._roomId,
      endpoint: summarizeRtcSfuEndpoint(this._dialogEndpoint),
      protooConnected: Boolean(this._protoo?.connected)
    });
    this._disconnecting = true;
    this._dialogAuthToken = null;
    this.cleanUpLocalState();
    if (this._protoo) {
      this._protoo.removeAllListeners();
      if (this._protoo.connected) {
        this._protoo.close();
        this.emitRTCEvent("info", "Signaling", () => `[close]`);
      }
    }
  }

  async kick(clientId) {
    await this.refreshAuthToken();

    return this._protoo
      .request("kick", {
        room_id: this.room,
        user_id: clientId,
        token: APP.hubChannel.token
      })
      .then(() => {
        document.body.dispatchEvent(new CustomEvent("kicked", { detail: { clientId: clientId } }));
      });
  }

  block(clientId) {
    return this._protoo.request("block", { whom: clientId }).then(() => {
      this._blockedClients.set(clientId, true);
      document.body.dispatchEvent(new CustomEvent("blocked", { detail: { clientId: clientId } }));
    });
  }

  unblock(clientId) {
    return this._protoo.request("unblock", { whom: clientId }).then(() => {
      this._blockedClients.delete(clientId);
      document.body.dispatchEvent(new CustomEvent("unblocked", { detail: { clientId: clientId } }));
    });
  }

  emitRTCEvent(level, tag, msgFunc) {
    if (!window.APP.store.state.preferences.showRtcDebugPanel) return;
    const time = new Date().toLocaleTimeString("en-US", {
      hour12: false,
      hour: "numeric",
      minute: "numeric",
      second: "numeric"
    });
    this.scene.emit("rtc_event", { level, tag, time, msg: msgFunc() });
  }
}
